import React, { useState, useEffect, useCallback } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField,
    Chip, CircularProgress, Alert, Table, TableHead, TableBody, TableRow, TableCell,
    FormControl, Select, MenuItem,
} from "@mui/material";
import Axios from "axios";
import { getAuthHeader } from "../../utils/authToken";
import { Field } from "./prepUi";
import AssignToField from "./AssignToField";

const V2_API = `${process.env.REACT_APP_URL}/api/v2`;

// JS getUTCDay(): 0=Sun..6=Sat. Presented Mon-first.
const WEEKDAYS = [
    { label: "Mon", day: 1 }, { label: "Tue", day: 2 }, { label: "Wed", day: 3 },
    { label: "Thu", day: 4 }, { label: "Fri", day: 5 }, { label: "Sat", day: 6 }, { label: "Sun", day: 0 },
];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Date math in UTC so calendar dates never shift under DST; emitted strings are NY wall-clock and
// the server converts to UTC (same approach as ScheduleExamsDialog).
const pad = (n) => String(n).padStart(2, "0");
const parseYmd = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const toYmd = (dt) => `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
const addDays = (dt, n) => new Date(dt.getTime() + n * 86400000);
const fmtSlot = (s) => {
    const [date, time] = s.split("T");
    const d = parseYmd(date);
    return `${DOW[d.getUTCDay()]} ${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()} · ${time}`;
};

// v2 test-category folders form a tree; flatten depth-first into an indented list for the dropdown.
const flattenFolders = (nodes, depth = 0, acc = []) => {
    (nodes || []).forEach((n) => {
        acc.push({ id: n.id, name: n.name, depth });
        if (n.children && n.children.length) flattenFolders(n.children, depth + 1, acc);
    });
    return acc;
};

// Bulk "schedule builder" for Test-Library tests: pick weekday(s) + release time + due offset +
// start date + week count → dated slots; pick that many library tests (in order) → create them all
// at once on one course (ALL or a SELECTED roster). Mirrors ScheduleExamsDialog.
const ScheduleV2TestsDialog = ({ open, onClose, programUuid, courses = [], existing = [], onCreated }) => {
    const base = `${process.env.REACT_APP_URL}/api/v1/prep-programs/${programUuid}`;
    const auth = { headers: { Authorization: getAuthHeader() } };

    const [courseId, setCourseId] = useState("");
    const [weekdays, setWeekdays] = useState([1]); // Mon
    const [releaseTime, setReleaseTime] = useState("18:00");
    const [dueOffsetDays, setDueOffsetDays] = useState(3);
    const [dueTime, setDueTime] = useState("23:59");
    const [startDate, setStartDate] = useState("");
    const [weeks, setWeeks] = useState(8);
    const [perDate, setPerDate] = useState(1); // tests sharing each release date

    const [folders, setFolders] = useState([]);
    const [folderId, setFolderId] = useState("");
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [libTests, setLibTests] = useState([]);
    const [libLoading, setLibLoading] = useState(false);
    const [selected, setSelected] = useState([]); // ordered [{ id, title }]
    const [assign, setAssign] = useState({ assign_mode: "ALL", student_ids: [] });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const courseName = (id) => {
        const c = courses.find((x) => x.id === id);
        return c ? `${c.label || c.course_title || "Course"}${c.role ? ` · ${c.role}` : ""}` : `Course ${id}`;
    };

    // Tests already assigned to the chosen course (can't schedule a duplicate the server would
    // drop), and the current max order on that course (so new rows append after existing ones).
    const onCourse = existing.filter((e) => e.prep_program_course_id === courseId);
    const addedIds = new Set(onCourse.map((e) => e.v2_test_id));
    const maxOrder = onCourse.reduce((m, e) => Math.max(m, e.order || 0), 0);

    const loadTests = useCallback(async () => {
        setLibLoading(true);
        try {
            const res = await Axios.get(`${V2_API}/tests`, { ...auth, params: { category_id: folderId || undefined, include_descendants: folderId ? true : undefined, search: debouncedSearch || undefined, limit: 300 } });
            setLibTests(res.data?.data || []);
        } catch (e) {
            setLibTests([]);
        } finally {
            setLibLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [folderId, debouncedSearch]);

    // Debounce the search box: fetch once typing pauses, not on every keystroke.
    useEffect(() => {
        const id = setTimeout(() => setDebouncedSearch(search), 300);
        return () => clearTimeout(id);
    }, [search]);

    useEffect(() => {
        if (!open) return;
        setCourseId(courses[0]?.id || "");
        setWeekdays([1]); setReleaseTime("18:00"); setDueOffsetDays(3); setDueTime("23:59");
        setStartDate(""); setWeeks(8); setPerDate(1); setFolderId(""); setSearch(""); setDebouncedSearch(""); setLibTests([]); setSelected([]);
        setAssign({ assign_mode: "ALL", student_ids: [] }); setError("");
        Axios.get(`${V2_API}/test-categories`, { ...auth, params: { tree: true } })
            .then((res) => setFolders(flattenFolders(res.data?.data || [])))
            .catch(() => setFolders([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => { if (open) loadTests(); }, [open, loadTests]);

    // Floor weeks to a non-negative integer so totalSlots is always an integer (a fractional
    // 'weeks' would otherwise make `selected.length === totalSlots` permanently unsatisfiable).
    const weekCount = Math.max(0, Math.floor(Number(weeks) || 0));
    // How many tests share each release date. Floor to a positive integer so totalSlots stays
    // an integer (a fractional value would make `selected.length === totalSlots` unsatisfiable).
    const perDateCount = Math.max(1, Math.floor(Number(perDate) || 1));
    const totalSlots = weekCount * weekdays.length * perDateCount;

    // Chronological release/due slots from the weekly pattern.
    const slots = (() => {
        if (!startDate || !weekCount || weekdays.length === 0) return [];
        const start = parseYmd(startDate);
        const out = [];
        weekdays.forEach((wd) => {
            let first = start;
            while (first.getUTCDay() !== wd) first = addDays(first, 1);
            for (let w = 0; w < weekCount; w++) {
                const rel = addDays(first, w * 7);
                const due = addDays(rel, Number(dueOffsetDays) || 0);
                out.push({ release: `${toYmd(rel)}T${releaseTime}`, due: `${toYmd(due)}T${dueTime}` });
            }
        });
        out.sort((a, b) => a.release.localeCompare(b.release));
        // Repeat each date perDateCount times, kept contiguous, so the tests picked for one date
        // all get the same release/due — letting several tests share a release date.
        if (perDateCount <= 1) return out;
        return out.flatMap((s) => Array.from({ length: perDateCount }, () => ({ ...s })));
    })();

    const toggleWeekday = (day) =>
        setWeekdays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
    const toggleTest = (t) => {
        setSelected((prev) => {
            const idx = prev.findIndex((s) => s.id === t.id);
            if (idx >= 0) return prev.filter((s) => s.id !== t.id);
            if (prev.length >= totalSlots) return prev; // cap at slot count
            return [...prev, { id: t.id, title: t.title }];
        });
    };

    // Append after any tests already on the course so re-running the builder doesn't interleave orders.
    const rows = slots.map((slot, i) => ({ slot, test: selected[i], order: maxOrder + 1 + i }));
    const rosterOk = assign.assign_mode !== "SELECTED" || (assign.student_ids?.length || 0) > 0;
    const ready = !!courseId && totalSlots > 0 && selected.length === totalSlots && slots.length === totalSlots && rosterOk;

    const create = async () => {
        setBusy(true); setError("");
        try {
            const payload = {
                prep_program_course_id: courseId,
                assign_mode: assign.assign_mode,
                student_ids: assign.assign_mode === "SELECTED" ? assign.student_ids : [],
                items: rows.map((r) => ({ v2_test_id: r.test.id, release_date: r.slot.release, due_date: r.slot.due, order: r.order })),
            };
            const res = await Axios.post(`${base}/v2-tests/bulk`, payload, auth);
            const created = res.data?.created ?? rows.length;
            if (onCreated) onCreated();
            // The server may drop a test (already added / already assigned to the course); don't
            // report success when a slot was silently left empty — surface it and stay open.
            if (created < rows.length) {
                const skipped = res.data?.skipped ?? 0;
                const conflicts = res.data?.conflicts ?? 0;
                // A conflict has a second remedy the scheduled path can't offer: adopting the
                // course's existing assignment. Point at it rather than only saying "remove".
                setError(`Only ${created} of ${rows.length} scheduled — ${skipped} already added, ${conflicts} already assigned to that course.`
                    + " Remove those rows to schedule the rest."
                    + (conflicts
                        ? ` The ${conflicts} already on the course can't be scheduled (they're already live) — add them from "Add tests" with "Adopt tests already on this course" ticked to pull in the work students have done.`
                        : ""));
                return;
            }
            onClose();
        } catch (e) {
            setError(e.response?.data?.error || "Failed to create tests");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Add tests on a schedule</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                    All times are Eastern Time (America/New_York).
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
                    <Field label="Assign to course">
                        <FormControl size="small" fullWidth>
                            <Select value={courseId} onChange={(e) => setCourseId(e.target.value)}>
                                {courses.map((c) => <MenuItem key={c.id} value={c.id}>{courseName(c.id)}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Field>

                    <Field label="Release on">
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                            {WEEKDAYS.map((wd) => (
                                <Chip key={wd.day} label={wd.label} size="small"
                                    color={weekdays.includes(wd.day) ? "primary" : "default"}
                                    variant={weekdays.includes(wd.day) ? "filled" : "outlined"}
                                    onClick={() => toggleWeekday(wd.day)} />
                            ))}
                        </Box>
                    </Field>

                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Field label="Release time (ET)">
                            <TextField type="time" size="small" value={releaseTime} onChange={(e) => setReleaseTime(e.target.value)} />
                        </Field>
                        <Field label="Due (days after)">
                            <TextField type="number" size="small" sx={{ width: 120 }} value={dueOffsetDays} onChange={(e) => setDueOffsetDays(e.target.value)} />
                        </Field>
                        <Field label="Due time (ET)">
                            <TextField type="time" size="small" value={dueTime} onChange={(e) => setDueTime(e.target.value)} />
                        </Field>
                    </Box>

                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="flex-end">
                        <Field label="Start date">
                            <TextField type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                        </Field>
                        <Field label="Weeks">
                            <TextField type="number" size="small" sx={{ width: 100 }} inputProps={{ min: 1, step: 1 }} value={weeks} onChange={(e) => setWeeks(e.target.value)} />
                        </Field>
                        <Field label="Tests per date">
                            <TextField type="number" size="small" sx={{ width: 120 }} inputProps={{ min: 1, step: 1 }} value={perDate} onChange={(e) => setPerDate(e.target.value)} />
                        </Field>
                        <Typography variant="body2" color="text.secondary" sx={{ pb: 1 }}>
                            = {totalSlots} test{totalSlots === 1 ? "" : "s"}{perDateCount > 1 ? ` (${perDateCount} per date)` : ""}
                        </Typography>
                    </Box>

                    <Field label={`Pick tests in order — ${selected.length}/${totalSlots} selected`}>
                        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <Select value={folderId} displayEmpty onChange={(e) => setFolderId(e.target.value)}>
                                    <MenuItem value="">All folders</MenuItem>
                                    {folders.map((f) => <MenuItem key={f.id} value={f.id} sx={{ pl: 2 + (f.depth || 0) * 2 }}>{f.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField size="small" placeholder="Search tests…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flexGrow: 1 }} />
                        </Box>
                        {libLoading ? (
                            <Box display="flex" justifyContent="center" py={2}><CircularProgress size={22} /></Box>
                        ) : libTests.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">No tests.</Typography>
                        ) : (
                            <Box sx={{ maxHeight: 180, overflow: "auto", display: "flex", flexWrap: "wrap", gap: 0.75, pr: 0.5 }}>
                                {libTests.map((t) => {
                                    const pick = selected.findIndex((s) => s.id === t.id);
                                    const isSel = pick >= 0;
                                    const added = addedIds.has(t.id); // already on this course → server would drop it
                                    return (
                                        <Chip key={t.id} size="small"
                                            label={isSel ? `${pick + 1}. ${t.title}` : added ? `${t.title} · added` : t.title}
                                            color={isSel ? "primary" : "default"}
                                            variant={isSel || added ? "filled" : "outlined"}
                                            disabled={!isSel && (added || selected.length >= totalSlots)}
                                            onClick={() => toggleTest(t)} />
                                    );
                                })}
                            </Box>
                        )}
                    </Field>

                    <Field label="Assign to">
                        <AssignToField programUuid={programUuid} rosterParams={{ program_course_id: courseId }}
                            value={assign} onChange={setAssign} />
                    </Field>

                    {rows.length > 0 && (
                        <Field label="Preview">
                            <Table size="small" sx={{ "& td, & th": { borderColor: "divider" } }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>#</TableCell>
                                        <TableCell>Test</TableCell>
                                        <TableCell>Release (ET)</TableCell>
                                        <TableCell>Due (ET)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map((r, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{r.order}</TableCell>
                                            <TableCell>{r.test ? r.test.title : <Typography variant="body2" color="text.disabled">pick a test</Typography>}</TableCell>
                                            <TableCell>{fmtSlot(r.slot.release)}</TableCell>
                                            <TableCell>{fmtSlot(r.slot.due)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Field>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={create} disabled={!ready || busy}>
                    {busy ? "Creating…" : `Create ${totalSlots} test${totalSlots === 1 ? "" : "s"}`}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ScheduleV2TestsDialog;
